import inherits from 'inherits-browser';
import CommandInterceptor from 'diagram-js/lib/command/CommandInterceptor';
import { isLabel } from 'diagram-js/lib/util/ModelUtil';
import { getLabel } from 'bpmn-js/lib/util/LabelUtil';

const PADDING = 7;
const MIN_WIDTH = 20;

/**
 * Ensures that label shapes are at least wide enough to contain the
 * longest single word of their text.
 *
 * Triggered after label text changes and after a user resizes a label.
 * If the longest word would otherwise overflow the label box, the label
 * is widened automatically.
 */
export default function LabelAutoSizeBehavior(
    eventBus, modeling, textRenderer
) {
  CommandInterceptor.call(this, eventBus);

  this._modeling = modeling;
  this._textRenderer = textRenderer;
  this._adjusting = false;

  const self = this;

  this.postExecuted('element.updateLabel', (event) => {
    const ctx = event.context;
    const element = ctx.element;
    const label = element && (isLabel(element) ? element : element.label);
    self._ensureMinWidth(label);
  });

  this.postExecuted('shape.resize', (event) => {
    const shape = event.context && event.context.shape;
    if (!shape || !isLabel(shape)) return;
    self._ensureMinWidth(shape);
  });
}

inherits(LabelAutoSizeBehavior, CommandInterceptor);

LabelAutoSizeBehavior.$inject = ['eventBus', 'modeling', 'textRenderer'];

LabelAutoSizeBehavior.prototype._ensureMinWidth = function(label) {
  if (!label || this._adjusting) return;

  const text = getLabel(label);
  if (!text) return;

  const style = this._textRenderer.getExternalStyle();
  const longestWord = measureLongestWord(text, style);
  if (!longestWord) return;

  const required = Math.max(MIN_WIDTH, Math.ceil(longestWord) + PADDING * 2);
  const currentWidth = Math.round(label.width || 0);

  if (currentWidth >= required) return;

  this._adjusting = true;
  try {
    const dx = required - currentWidth;
    this._modeling.resizeShape(label, {
      x: Math.round(label.x - dx / 2),
      y: label.y,
      width: required,
      height: label.height
    });
  } finally {
    this._adjusting = false;
  }
};

let measureCanvas = null;

function measureLongestWord(text, style) {
  if (typeof document === 'undefined') return 0;

  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas');
  }
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) return 0;

  const fontSize = parseInt(style.fontSize, 10) || 12;
  const fontFamily = style.fontFamily || 'Arial, sans-serif';
  const fontWeight = style.fontWeight || 'normal';
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

  // Split on whitespace, line breaks and soft hyphens — these are the points
  // at which the layout engine may break a line. Anything in between is one
  // unbreakable token.
  const tokens = String(text).split(/[\s\u00AD\r\n]+/).filter(Boolean);

  let max = 0;
  for (const tok of tokens) {
    const w = ctx.measureText(tok).width;
    if (w > max) max = w;
  }
  return max;
}
